import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import { createStore } from "redux";
import type { Subject } from "rxjs";
import { from } from "rxjs";
import { distinctUntilChanged, filter, switchMap } from "rxjs/operators";
import rootReducer from "../../legacy/reducers";
import { questionnaireService } from "../../legacy/services/questionnaire/questionnaire.service.instance";
import { handleQuestionnaire } from "../../legacy/stores/questionnaire/handleQuestionnaire";
import { trackQuestionnairePersistence } from "../../legacy/stores/questionnaire/questionnaire-persistence-tracker";
import { questionnaireSubject$ } from "../../legacy/stores/questionnaireSubject";
import { serializeForDiffCheck } from "../../legacy/utils/serializeForDiffCheck";

const isDevelopmentMode = process.env.BUILD_MODE === "development";

const devToolsConfig = {
  trace: true,
  traceLimit: 25,
};

const store = createStore(
  rootReducer,
  isDevelopmentMode ? window.__REDUX_DEVTOOLS_EXTENSION__?.(devToolsConfig) : undefined
);

function areCitizenNameAndEmailProvided(current: RootStoreModule.State): boolean {
  const citizenName = current.formValues.Nome;
  const citizenEmail = current.formValues.email;
  return !isNil(citizenName) && !isNil(citizenEmail);
}

const questionnairePersist$ = questionnaireSubject$.pipe(
  distinctUntilChanged(function isDiffState(previous, current) {
    const currentHasCitizenNameAndEmail = areCitizenNameAndEmailProvided(current);
    if (currentHasCitizenNameAndEmail) {
      const isTheSame = isEqual(serializeForDiffCheck(previous.formValues), serializeForDiffCheck(current.formValues));
      return isTheSame;
    }
    return !currentHasCitizenNameAndEmail;
  }),
  filter((data: RootStoreModule.State) => {
    const isInitialized = data.questionnaireHasBeenDownloaded;
    return isInitialized;
  }),
  switchMap((data) => {
    const handledData = handleQuestionnaire(data);
    trackQuestionnairePersistence(handledData);
    return from(handledData);
  })
);

export function subscribeToQuestionnairePersist(questionnaireSubject$: Subject<RootStoreModule.State>) {
  questionnaireService.unsubscribeAndClear();

  const subscription = questionnairePersist$.subscribe({
    next: function onResponseReceived(data) {
      console.debug("Questionnaire response:", data);
    },
    error: function onError(error) {
      console.error("Questionnaire error:", error);
      subscribeToQuestionnairePersist(questionnaireSubject$);
    },
    complete: function onComplete() {
      console.debug("Questionnaire stop listen");
      subscribeToQuestionnairePersist(questionnaireSubject$);
    },
  });

  questionnaireService.bindSubscription(subscription);
}

subscribeToQuestionnairePersist(questionnaireSubject$);

store.subscribe(function onSetQuestionnaire() {
  const currentState = store.getState().updates;

  questionnaireSubject$.next(currentState);
});

export default store;
